import {CheckOutlined, ClockCircleOutlined, CloseOutlined, LoadingOutlined, WarningOutlined} from '@ant-design/icons';
import {Button, Flex, Form, Input, List, Modal, Tooltip} from 'antd';
import React, {useEffect, useRef, useState} from 'react';

import {usePostAdminPrintLabelMutation} from '../../../../gen/api/apis';
import {AdminPrintLabelResponse} from '../../../../gen/api/models';

export type BatchLabelPrintProps = {
    products: PrintableProducts[];
    close: () => void;
};

export type PrintableProducts = {
    asin: string;
    description: string;
};

enum Status {
    created = 'created',
    inProgress = 'inProgress',
    completed = 'completed',
    failed = 'failed',
}

enum ProcessStatus {
    idle = 'idle',
    processing = 'processing',
    completed = 'completed',
}

type ProductForPrint = {
    id: string;
    asin: string;
    description: string;
    errorMessage?: string;
    status: Status;
};

export const BatchLabelPrint = (props: BatchLabelPrintProps) => {
    const [products, setProducts] = useState<ProductForPrint[]>([]);
    const [status, setStatus] = useState<ProcessStatus>(ProcessStatus.idle);
    const isProcessingRef = useRef(false);
    const [form] = Form.useForm();
    const url = Form.useWatch('url', form);

    useEffect(() => {
        form.setFieldValue('url', localStorage.getItem('printLabelUrl'));
    }, []);

    useEffect(() => {
        const newProducts = props.products.map((product) => ({
            id: Math.random().toString(36).substring(2, 15),
            asin: product.asin,
            description: product.description,
            status: Status.created,
        }));
        setProducts(newProducts);
    }, []);

    const printRequest = usePostAdminPrintLabelMutation();

    useEffect(() => {
        if (status === ProcessStatus.processing && !isProcessingRef.current) {
            processQueue();
        }
    }, [status]);

    async function processQueue() {
        isProcessingRef.current = true;

        const productsToPrint = products.filter((product) => product.status === Status.created);
        for (let i = 0; i < productsToPrint.length; i++) {
            if (status !== ProcessStatus.processing) break;

            const item = productsToPrint[i];

            updateItemStatus(item.id, Status.inProgress);

            await printRequest.mutateAsync(
                {
                    params: {
                        url: url!,
                        asin: item.asin,
                        description: item.description,
                    },
                },
                {
                    onSuccess: (data: AdminPrintLabelResponse) => {
                        // update product status to completed
                        updateItemStatus(item.id, data.success ? Status.completed : Status.failed, data.message);
                    },
                    onError: () => {
                        // update product status to failed
                        updateItemStatus(item.id, Status.failed, 'Failed to print label');
                    },
                }
            );
        }
        setStatus(ProcessStatus.completed);

        isProcessingRef.current = false;
    }

    function updateItemStatus(id: string, status: Status, message?: string) {
        setProducts((prev) => {
            return prev.map((item) => {
                if (item.id === id) {
                    return {
                        ...item,
                        status: status,
                        errorMessage: message,
                    };
                }
                return item;
            });
        });
    }

    const handleStatusTransition = () => {
        form.validateFields({validateOnly: true})
            .then(() => {
                localStorage.setItem('printLabelUrl', url);
                const STATUS_TRANSITIONS = {
                    [ProcessStatus.idle]: ProcessStatus.processing,
                    [ProcessStatus.processing]: ProcessStatus.completed,
                    [ProcessStatus.completed]: null,
                } as const;

                const nextStatus = STATUS_TRANSITIONS[status];

                if (nextStatus === null) {
                    props.close();
                } else {
                    setStatus(nextStatus);
                }
            })
            .catch(() => {
                console.error('Validation failed');
            });
    };

    return (
        <Modal
            open={true}
            closable={status !== ProcessStatus.processing}
            onClose={() => {
                props.close();
            }}
            onCancel={() => props.close()}
            destroyOnClose={true}
            footer={null}
            title={'Batch Label Print'}
            width={800}
        >
            <Flex
                justify={'center'}
                gap={10}
            >
                <Form
                    disabled={status === ProcessStatus.processing}
                    layout="horizontal"
                    labelCol={{span: 5}}
                    wrapperCol={{span: 19}}
                    form={form}
                >
                    <Form.Item
                        name="url"
                        label="URL"
                        rules={[{required: true, message: 'Please input url!'}]}
                    >
                        <Input placeholder="input url" />
                    </Form.Item>
                </Form>
                <Button
                    onClick={handleStatusTransition}
                    color={status === ProcessStatus.idle || status === ProcessStatus.completed ? 'primary' : 'default'}
                    loading={status === ProcessStatus.processing}
                >
                    {status === ProcessStatus.idle
                        ? 'Start Printing'
                        : status === ProcessStatus.processing
                          ? 'Processing...'
                          : 'Close'}
                </Button>
                {status === ProcessStatus.processing && (
                    <Button
                        style={{color: 'red'}}
                        icon={
                            <CloseOutlined
                                color={'red'}
                                style={{color: 'red'}}
                            />
                        }
                        onClick={() => setStatus(ProcessStatus.completed)}
                    />
                )}
            </Flex>
            <List
                style={{marginTop: 16}}
                dataSource={products}
                renderItem={(item: ProductForPrint) => (
                    <List.Item>
                        <Flex
                            align={'center'}
                            style={{overflow: 'hidden', textOverflow: 'ellipsis'}}
                            gap={5}
                        >
                            <Tooltip title={item.errorMessage}>
                                {item.status === Status.created ? (
                                    <ClockCircleOutlined
                                        style={{color: 'grey'}}
                                        size={100}
                                    />
                                ) : item.status === Status.inProgress ? (
                                    <LoadingOutlined style={{color: 'blue'}} />
                                ) : item.status === Status.completed ? (
                                    <CheckOutlined style={{color: 'green'}} />
                                ) : (
                                    <WarningOutlined style={{color: 'red'}} />
                                )}
                            </Tooltip>

                            <div style={{whiteSpace: 'nowrap'}}>{item.asin + ' ' + item.description}</div>
                        </Flex>
                    </List.Item>
                )}
            />
        </Modal>
    );
};
